/**
 * economy system — v0M3.2 first pass implementation.
 *
 * Per-tick pass over every Province + owning Faction that applies the
 * v0M1.9.16 economy model (`ECONOMY.md` §1-§4):
 *
 *   1. Population food consumption + growth (food-gated, tier-capped).
 *   2. Gold-from-citizens (0.0001 G/s per citizen per province).
 *   3. Structure production (1 resource/s per structure at v1 lock).
 *   4. Build-queue advancement + completion.
 *
 * Timing: all rates in `ECONOMY.md` are per game-time-second; each
 * tick represents `SECONDS_PER_TICK` (= 0.2) game-time seconds per
 * `MECHANICS.md` §1.1. All accumulations here multiply by that constant.
 *
 * Determinism: iteration order is by sortedIds() per `MECHANICS.md`
 * §1.1 rule 3. Nothing here consumes RNG at v0M3.2 first pass; famine
 * decay + capture events land later.
 *
 * Balance data (structure production rates, gold cost, dial values)
 * are loaded once at module scope from the xlsx pipeline
 * (`src/data/*`). If a value needs to change per campaign, refactor to
 * a SimContext field; module-scope constants match the SG pattern for
 * "campaign-invariant balance dials".
 */

import type { Command } from './commands';
import type { GameState } from './state';
import type { RngState } from './rng';
import type { SimContext } from './sim-context';
import {
  SECONDS_PER_TICK,
  TICKS_PER_GAME_DAY,
  createArmy,
  createCommander,
  createSpy,
  sortedIds,
  toArmyId,
  toCohortId,
  toCommanderId,
  toSpyId,
} from './state';
import type {
  Cohort,
  Faction,
  Province,
  SettlementTier,
  RecruitmentOrder,
  Resources,
  StructureBuild,
} from './state';
import { loadStructures } from '../data/structures';
import type { StructureSpec } from '../data/structures';
import { loadCohorts } from '../data/cohorts';
import { loadSpecialUnits } from '../data/special-units';
import { loadMapStructures } from '../data/map-structures';
import { loadDials, getDialNumber } from '../data/dials';
import { loadKnowledge } from '../data/knowledge';
import { recruitSpeedMultiplier } from './knowledge-effects';
import { recruitArmorBonus, commanderXpPerDayBonus, OFFICER_ACADEMY_LEVEL_CAP } from './structure-effects';
// v0M10.6 (2026-07-24) — xlsx-authoritative commander level curve.
import { loadCommanderLevels, commanderLevelForXp } from '../data/commander-levels';
const ECON_COMMANDER_LEVELS = loadCommanderLevels();

// ---------------------------------------------------------------------------
// Balance data (loaded once at module init — same pattern as SG)
// ---------------------------------------------------------------------------

const STRUCTURES = loadStructures();
const COHORTS = loadCohorts();
// v0M5.4d (2026-07-25) — SpecialUnits xlsx loaded once at module init.
// Consumed by stepRecruitQueue when RecruitmentOrder.specialUnitType is
// set: `recruitTimeDays` gives the completion threshold, spawn happens
// via spawnSpy(). Empty at module load is fine — the special-unit branch
// simply drops any orders whose type is missing.
const SPECIAL_UNITS = loadSpecialUnits();
const DIALS = loadDials();
// v0M9.10 (2026-07-24) — Knowledge loaded once for effect lookups
// during stepRecruitQueue. Faction-scoped multiplier applied per province.
const KNOWLEDGE = loadKnowledge();

// Economy scalars per ECONOMY.md §7 dial sheet (values in `Balance.xlsx`).
// Read once at module init; balance revisions require xlsx edit + data:build.
const GOLD_PER_CITIZEN_PER_SEC = getDialNumber(DIALS, 'gold_per_citizen_per_sec');
const SUPPLIES_PER_CITIZEN_PER_SEC = getDialNumber(DIALS, 'supplies_per_citizen_per_sec');
const POP_GROWTH_RATE_PER_SEC = getDialNumber(DIALS, 'pop_growth_rate_per_sec');

// v0M3.11 cohort recovery dials — per-day recovery rates.
// MFix3.8: TICKS_PER_GAME_DAY became 25 (was 5); per-tick math is now
// `per-day / TICKS_PER_GAME_DAY` explicitly (was `per-day × SECONDS_PER_TICK`
// which numerically coincided with `/ 5` under the old model). Recovery
// per-game-day dial values unchanged in `Balance.xlsx`; only the tick-
// scale distribution changes to match the new day duration.
const STAMINA_RECOVERY_PER_DAY = getDialNumber(DIALS, 'cohort_stamina_recovery_per_day');
const MORALE_RECOVERY_PER_DAY = getDialNumber(DIALS, 'cohort_morale_recovery_per_day');
const STAMINA_RECOVERY_PER_TICK = STAMINA_RECOVERY_PER_DAY / TICKS_PER_GAME_DAY;
const MORALE_RECOVERY_PER_TICK = MORALE_RECOVERY_PER_DAY / TICKS_PER_GAME_DAY;
// MFix6.3 (2026-07-23) — recovery bonus multiplier applied to army
// cohorts sitting inside a friendly city's ZoC (Chebyshev distance ≤
// settlement_zoc_radius). Encourages the player to rest wounded armies near
// their cities rather than deep in enemy territory.
const FRIENDLY_CITY_RECOVERY_MULT = getDialNumber(DIALS, 'friendly_settlement_zoc_recovery_multiplier');
const SETTLEMENT_ZOC_RADIUS = getDialNumber(DIALS, 'settlement_zoc_radius');
// v0M7.4d (2026-07-25) — Fort ZoC radius per MAP.md §9.2. Read from
// MapStructures xlsx if present, else fall back to settlement radius
// so Forts get the same supply-zone footprint. Design default from
// MAP.md §9.4 = 4 cells.
const FORT_ZOC_RADIUS = ((): number => {
  const fortSpec = loadMapStructures()['Fort'];
  if (fortSpec && fortSpec.zocRadius > 0) return fortSpec.zocRadius;
  return SETTLEMENT_ZOC_RADIUS;
})();
// MFix6.6.2 (2026-07-23) — manpower generation + cap.
const MANPOWER_GEN_PER_POP_PER_DAY = getDialNumber(DIALS, 'manpower_generation_per_pop_per_day');
const MANPOWER_CAP_FRACTION_OF_POP = getDialNumber(DIALS, 'manpower_cap_fraction_of_pop');
// MFix6.6.4 (2026-07-23) — figure replenishment rate per cohort per game-day.
const MANPOWER_REPLENISH_FIGURES_PER_DAY = getDialNumber(DIALS, 'manpower_replenish_figures_per_day_per_cohort');
const REPLENISH_PER_TICK = MANPOWER_REPLENISH_FIGURES_PER_DAY / TICKS_PER_GAME_DAY;
// v0M10.1 (2026-07-24) — Army supply dials. Per-day rates converted to
// per-tick constants at module init.
const ARMY_SUPPLY_CAP = getDialNumber(DIALS, 'army_supply_cap');
const ARMY_SUPPLY_CONSUMPTION_PER_FIGURE_PER_DAY = getDialNumber(DIALS, 'army_supply_consumption_per_figure_per_day');
const ARMY_SUPPLY_REGEN_PER_DAY = getDialNumber(DIALS, 'army_supply_regen_per_day');
const ARMY_SUPPLY_CONSUMPTION_PER_FIGURE_PER_TICK = ARMY_SUPPLY_CONSUMPTION_PER_FIGURE_PER_DAY / TICKS_PER_GAME_DAY;
const ARMY_SUPPLY_REGEN_PER_TICK = ARMY_SUPPLY_REGEN_PER_DAY / TICKS_PER_GAME_DAY;
// v0M10.4 (2026-07-24) — starvation drain rates. Phase 1: morale +
// stamina drain (independent dials so each can be tuned). Phase 2:
// shared per-cohort starvationHp drains; when it reaches 0, the
// cohort's figures all die (aliveFigureCount = 0).
const STARVATION_MORALE_DRAIN_PER_DAY = getDialNumber(DIALS, 'starvation_morale_drain_per_day');
const STARVATION_STAMINA_DRAIN_PER_DAY = getDialNumber(DIALS, 'starvation_stamina_drain_per_day');
const STARVATION_HP_DRAIN_PER_DAY = getDialNumber(DIALS, 'starvation_hp_drain_per_day');
const STARVATION_MORALE_DRAIN_PER_TICK = STARVATION_MORALE_DRAIN_PER_DAY / TICKS_PER_GAME_DAY;
const STARVATION_STAMINA_DRAIN_PER_TICK = STARVATION_STAMINA_DRAIN_PER_DAY / TICKS_PER_GAME_DAY;
const STARVATION_HP_DRAIN_PER_TICK = STARVATION_HP_DRAIN_PER_DAY / TICKS_PER_GAME_DAY;
// MFix8.1 / MFix8.2 (2026-07-24) — concurrency caps on the recruit + build
// queues. Only the first N orders accrue progress each tick; the rest wait.
// Structure-boostable post-v1 (planned dial hookup once structures like
// Barracks / Foundry land). Default 1 keeps the player's economy interesting.
const RECRUIT_CONCURRENT_CAP = Math.max(1, Math.floor(getDialNumber(DIALS, 'settlement_concurrent_recruit_cap')));
const BUILD_CONCURRENT_CAP = Math.max(1, Math.floor(getDialNumber(DIALS, 'settlement_concurrent_build_cap')));

/** Population caps per tier from ECONOMY.md §2.1. */
const POP_CAP_BY_TIER: Record<SettlementTier, number> = {
  Village: getDialNumber(DIALS, 'village_pop_cap'),
  Town: getDialNumber(DIALS, 'town_pop_cap'),
  City: getDialNumber(DIALS, 'city_pop_cap'),
};

// ---------------------------------------------------------------------------
// Public: system tick function
// ---------------------------------------------------------------------------

export function economySystem(
  state: GameState,
  _commands: readonly Command[],
  _rng: RngState,
  context: SimContext,
): GameState {
  for (const provinceId of sortedIds(state.provinces)) {
    const province = state.provinces.get(provinceId)!;
    if (province.ownerFactionId === null) continue; // Unowned provinces don't feed a faction ledger.

    const faction = state.factions.get(province.ownerFactionId);
    if (!faction) continue;

    // v0M4.2.2 siege denial gate per BATTLES.md §3.4. If ANY city that
    // lives in this province is under active siege, the production +
    // growth + queue-advance steps for that province are skipped for
    // this tick. Rationale: at v1 provinces are 1:1 with capital cities,
    // so a besieged capital gates the whole province. When minor cities
    // are materialized post-MVP, the gate refines to per-city.
    if (isProvinceUnderSiege(state, province.id)) continue;

    // Order per ECONOMY.md model:
    //   1. Food consumption + growth check (province-scope).
    //   2. Gold + structure production (add to faction ledger).
    //   3. Build queue advance + complete.
    //   4. Recruit queue advance + spawn cohorts on completion.
    stepPopulation(province, faction);
    stepProvinceProduction(province, faction, state);
    stepBuildQueue(province, state.tick);
    stepRecruitQueue(province, state);
  }
  // Step 5 (v0M3.11) — cohort recovery. Iterates cohorts, not provinces,
  // so it lives outside the province loop.
  stepCohortRecovery(state, context);
  // Step 6 (MFix6.6.2 — 2026-07-23) — manpower generation. Iterates
  // cities, not provinces, so it lives outside the province loop.
  // Runs AFTER stepPopulation so this tick's population growth is
  // visible in the cap math.
  stepManpowerGeneration(state);
  // Step 7 (MFix6.6.4 — 2026-07-23) — figure replenishment for army
  // cohorts sitting in a friendly city's ZoC. Draws manpower from the
  // closest matching-race friendly city.
  stepFigureReplenishment(state);
  // Step 8 (v0M10.1 — 2026-07-24) — army supply. Regenerates supply
  // (drawing from the owning faction's global supplies stockpile) when
  // the army sits in a friendly settlement ZoC; otherwise consumes
  // per-figure. Silent when supply hits 0 at v1 — attrition / morale
  // consequences deferred to a later MFix.
  stepArmySupply(state);
  // Step 9 (v0M10.5 — 2026-07-24) — Officer Academy XP tick. Adds
  // `Commander XP + N per day` bonuses to any commander whose army
  // sits in a friendly province with the Officer Academy structure,
  // capped at `OFFICER_ACADEMY_LEVEL_CAP` (5 at v1).
  stepCommanderStructureXp(state);
  return state;
}

/**
 * v0M4.2.2 — true iff any Settlement in `provinceId` currently has an active
 * siege. Iterates cities (small N at v1 — 6 factions × 1 capital = 6
 * cities typical). Called per-province each economy tick before the
 * production loop; skipping is cheap even in the false case.
 */
function isProvinceUnderSiege(state: GameState, provinceId: import('./state').ProvinceId): boolean {
  for (const city of state.settlements.values()) {
    if (city.provinceId !== provinceId) continue;
    if (city.siegeState !== null) return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Step 1 — Population food consumption + growth
// ---------------------------------------------------------------------------

/**
 * Apply this province's food consumption to the faction Food ledger and
 * decide whether growth applies this tick per ECONOMY.md §2.2.
 *
 * v0M3.2 rule: if the faction has enough Food to cover this province's
 * per-tick consumption, deduct it and apply the 0.2%-per-second growth
 * (capped at tier pop cap). Otherwise Food goes to zero and growth
 * halts for this tick (no shrinkage — first-playtest dial candidate
 * per ECONOMY.md §2.3 item 2).
 */
function stepPopulation(province: Province, faction: Faction): void {
  const consumptionPerTick = province.population * SUPPLIES_PER_CITIZEN_PER_SEC * SECONDS_PER_TICK;

  if (faction.resources.supplies >= consumptionPerTick) {
    // Surplus: deduct food, apply growth.
    faction.resources.supplies -= consumptionPerTick;
    const growth = province.population * POP_GROWTH_RATE_PER_SEC * SECONDS_PER_TICK;
    const cap = POP_CAP_BY_TIER[province.tier];
    // MFix3.1: floor the stored population so downstream display never
    // shows a fractional value. Growth still accumulates via the
    // deterministic multiplicative model; only the visible integer
    // representation is stored. Downside: sub-1-per-tick growth on very
    // small populations quantizes to 0 — playtest whether this matters.
    province.population = Math.floor(Math.min(cap, province.population + growth));
  } else {
    // Deficit: drain remaining food to zero, halt growth this tick.
    faction.resources.supplies = 0;
  }
}

// ---------------------------------------------------------------------------
// Step 2 — Province production (Gold from citizens + structure output)
// ---------------------------------------------------------------------------

function stepProvinceProduction(province: Province, faction: Faction, state: GameState): void {
  // Gold from citizens per ECONOMY.md §1.
  const goldTick = province.population * GOLD_PER_CITIZEN_PER_SEC * SECONDS_PER_TICK;
  faction.resources.gold += goldTick;

  // Structure output per ECONOMY.md §3.1 — every completed structure
  // produces `ratePerSec × SECONDS_PER_TICK` of its resource per tick.
  // v0M9 (2026-07-27) — structures attributed to a pillaged minor
  // settlement produce no income until the minor is repaired.
  for (const built of province.structures) {
    const spec = STRUCTURES[built.name];
    if (!spec) continue; // Unknown name in a save: skip and let load-time validation flag.
    if (isStructurePillaged(state, built)) continue;
    addStructureProduction(faction.resources, spec, SECONDS_PER_TICK);
  }
}

/**
 * v0M9 (2026-07-27) — True if this structure was built at a minor
 * settlement that is currently `pillaged`. Structures without a
 * `settlementId` attribution (majority — they live at the Province
 * Capital) are never gated by this helper. Cheap: single Map get on
 * settlements when settlementId is present.
 */
function isStructurePillaged(state: GameState, built: StructureBuild): boolean {
  const sid = built.settlementId;
  if (sid === undefined) return false;
  const settlement = state.settlements.get(sid);
  if (!settlement) return false;
  return settlement.pillaged === true;
}

/**
 * Add one structure's per-tick production to a faction resource ledger.
 * Uses a small dispatch on the produces field — matches the six
 * resource keys in `Resources`.
 */
function addStructureProduction(
  resources: Resources,
  spec: StructureSpec,
  seconds: number,
): void {
  const amount = spec.ratePerSec * seconds;
  switch (spec.produces) {
    case 'Gold': resources.gold += amount; break;
    case 'Iron': resources.iron += amount; break;
    case 'Wood': resources.wood += amount; break;
    case 'Horses': resources.horses += amount; break;
    case 'Food': resources.supplies += amount; break;
    case 'Supplies': resources.supplies += amount; break;
    case 'Learning': resources.learning += amount; break;
    // Population is per-province, not on the faction ledger — Structure
    // spec should never produce it. Unknown resources are silently
    // ignored (a Balance.xlsx `produces` typo won't crash the tick).
    default: break;
  }
}

// ---------------------------------------------------------------------------
// Step 3 — Build queue advancement + completion
// ---------------------------------------------------------------------------

function stepBuildQueue(province: Province, tick: number): void {
  if (province.buildQueue.length === 0) return;

  // MFix8.2 (2026-07-24) — only the first N orders in the queue accrue
  // progress each tick (N = BUILD_CONCURRENT_CAP, dial-tuned to 1 at v1
  // start). Orders past index N-1 sit waiting; they slot into position
  // when a leading order completes and shifts the queue forward.
  //
  // Retires the pre-MFix8 parallel-advance rule (`ECONOMY.md §4.2 item 13`
  // serial-queue candidate is now the shipping design). Structure-boostable
  // post-v1 (Barracks etc. would raise the cap).
  const completed: StructureBuild[] = [];
  const stillInProgress: typeof province.buildQueue = [];
  const activeCount = Math.min(BUILD_CONCURRENT_CAP, province.buildQueue.length);
  for (let i = 0; i < province.buildQueue.length; i++) {
    const order = province.buildQueue[i]!;
    if (i < activeCount) {
      order.progressSec += SECONDS_PER_TICK;
    }
    const spec = STRUCTURES[order.name];
    if (spec && order.progressSec >= spec.buildTimeSec) {
      // v0M9-followup.1 — propagate settlementId attribution so pillage
      // income halt works on minor-attributed structures.
      completed.push({
        name: order.name,
        builtAtTick: tick,
        ...(order.settlementId !== undefined ? { settlementId: order.settlementId } : {}),
      });
    } else {
      stillInProgress.push(order);
    }
  }

  // Replace the queue contents (kept mutable so simple splice semantics work).
  province.buildQueue.length = 0;
  province.buildQueue.push(...stillInProgress);
  if (completed.length > 0) {
    province.structures.push(...completed);
  }
}

// ---------------------------------------------------------------------------
// Step 4 — Recruit queue advancement + cohort spawn (v0M3.2a)
// ---------------------------------------------------------------------------

function stepRecruitQueue(province: Province, state: GameState): void {
  if (province.recruitQueue.length === 0) return;

  // MFix8.1 (2026-07-24) — recruit concurrency cap (default 1). Only the
  // first N orders in the queue accrue progress; the rest wait behind
  // them. Structure-boostable post-v1.
  //
  // v0M9.10 (2026-07-24) — apply the Improved Training Techniques
  // multiplier to per-tick progress. Multiplier is 1.0 by default;
  // ranges up to ~1.43× when tier III (-30%) is researched. Faction-scoped
  // so tests without research get the identical old behaviour.
  const faction = province.ownerFactionId !== null ? state.factions.get(province.ownerFactionId) : null;
  const speedMult = faction ? recruitSpeedMultiplier(faction, KNOWLEDGE) : 1;
  const perTickProgress = SECONDS_PER_TICK * speedMult;

  const completed: RecruitmentOrder[] = [];
  const stillInProgress: RecruitmentOrder[] = [];
  const activeCount = Math.min(RECRUIT_CONCURRENT_CAP, province.recruitQueue.length);
  for (let i = 0; i < province.recruitQueue.length; i++) {
    const order = province.recruitQueue[i]!;
    if (i < activeCount) {
      order.progressSec += perTickProgress;
    }
    // v0M5.4d (2026-07-25) — special-unit branch. When
    // `specialUnitType` is set, the SpecialUnits xlsx spec supplies
    // the completion threshold (days → seconds via
    // TICKS_PER_GAME_DAY × SECONDS_PER_TICK). The cohortType field is
    // an empty string in this branch and is not consulted.
    if (order.specialUnitType !== undefined && order.specialUnitType !== '') {
      const spec = SPECIAL_UNITS[order.specialUnitType];
      if (!spec) {
        // Missing spec → drop the order (fixture / mis-authored xlsx).
        continue;
      }
      const thresholdSec = spec.recruitTimeDays * TICKS_PER_GAME_DAY * SECONDS_PER_TICK;
      if (order.progressSec >= thresholdSec) {
        completed.push(order);
      } else {
        stillInProgress.push(order);
      }
      continue;
    }
    const spec = COHORTS[order.cohortType];
    if (spec && order.progressSec >= spec.recruitTimeSec) {
      completed.push(order);
    } else {
      stillInProgress.push(order);
    }
  }
  province.recruitQueue.length = 0;
  province.recruitQueue.push(...stillInProgress);

  for (const order of completed) {
    if (order.specialUnitType !== undefined && order.specialUnitType !== '') {
      spawnSpy(state, order);
    } else {
      spawnCohort(state, province, order);
    }
  }
}

/**
 * v0M5.4d (2026-07-25) — Special-unit spawn (Spy at v1).
 *
 * Mints a SpyId from `state.nextSpyId` (initialised to 1 on first use
 * for back-compat with pre-v0M5 GameState fixtures), creates the Spy
 * at the recruiting settlement's cell centre (0.5 offset mirrors
 * `createArmy` semantics — spies live at cell centres like armies),
 * and inserts it into `state.spies`. `state.spies` is optional on
 * GameState (v0M5 back-compat); initialise the Map on first spawn.
 *
 * Silently drops the order if the recruiting settlement no longer
 * exists (city fell + was destroyed between queue and spawn) — same
 * unrefundable-loss policy as garrison recruit branch 1 in spawnCohort.
 */
function spawnSpy(state: GameState, order: RecruitmentOrder): void {
  if (order.settlementId === null) return;
  const settlement = state.settlements.get(order.settlementId);
  if (!settlement) return;
  const province = state.provinces.get(settlement.provinceId);
  if (!province) return;
  const factionId = province.ownerFactionId;
  if (factionId === null) return;
  const specialUnitType = order.specialUnitType!;
  if (state.nextSpyId === undefined || state.nextSpyId < 1) {
    state.nextSpyId = 1;
  }
  const spyId = toSpyId(state.nextSpyId);
  state.nextSpyId += 1;
  const spy = createSpy(spyId, factionId, specialUnitType, {
    x: settlement.cell.x + 0.5,
    y: settlement.cell.y + 0.5,
  });
  if (!state.spies) state.spies = new Map();
  state.spies.set(spyId, spy);
}

/**
 * Complete a recruitment. Three branches, keyed on the (armyId, settlementId)
 * pair carried on the RecruitmentOrder:
 *
 *   1. `settlementId !== null` → **garrison recruit** (v0M4.2.1 per BATTLES.md
 *      §3.2). Spawns a Cohort with `owner.kind === 'city'`, adds it to
 *      the target city's `garrisonCohortIds`. No army is created or
 *      touched. If the target city has been destroyed since the recruit
 *      was queued, the cohort is dropped.
 *   2. `armyId !== null` → **attach to existing army**. Spawns a Cohort
 *      with `armyId` set + pushed onto `army.cohortIds`. If the target
 *      army has been destroyed since queue, falls through to branch 3
 *      (create-new-army fallback).
 *   3. `armyId === null && settlementId === null` → **create new army**.
 *      Fresh Default Commander + Army placed on the recruiting
 *      province's capital city cell (v0M3.11 spawn-cell fix).
 */
function spawnCohort(state: GameState, province: Province, order: RecruitmentOrder): void {
  const spec = COHORTS[order.cohortType];
  if (!spec) return;

  const factionId = province.ownerFactionId;
  if (factionId === null) return;

  // v0M9.13 (2026-07-24) — snapshot the armor bonus from the recruiting
  // province's built structures. Applied to army and garrison cohorts
  // alike (Armorers Guild at v1). Bakes in at recruit-completion time
  // per the "recruited here" semantics — subsequent Guild changes do
  // not retroactively affect this cohort.
  const armorBonus = recruitArmorBonus(province, STRUCTURES);

  // Branch 1 — settlement garrison recruit.
  if (order.settlementId !== null) {
    const city = state.settlements.get(order.settlementId);
    if (!city) return; // Target city gone — drop the cohort (unrefundable).
    const cohortId = toCohortId(state.nextCohortId++);
    const garrison: Cohort = {
      id: cohortId,
      factionId,
      armyId: null,
      settlementId: order.settlementId,
      type: order.cohortType,
      aliveFigureCount: spec.figureCount,
      stamina: 100,
      morale: 100,
      xp: 0,
      armorBonus,
    };
    state.cohorts.set(cohortId, garrison);
    city.garrisonCohortIds.push(cohortId);
    return;
  }

  // Branch 4 — Fort garrison recruit (v0M7.4d+garrison, 2026-07-26).
  // Mirror of Branch 1 but targets a WorldStructure of kind='fort'
  // instead of a Settlement. If the Fort is gone (destroyed or
  // captured post-queue), drop the cohort — no refund. Fort ownership
  // may have flipped mid-queue; only accept if the Fort still belongs
  // to the recruiting faction.
  const fortTargetId = order.worldStructureId ?? null;
  if (fortTargetId !== null) {
    const fort = state.worldStructures?.get(fortTargetId);
    if (!fort) return; // Target Fort gone.
    if (fort.kind !== 'fort') return; // Target no longer a Fort (schema drift).
    if (fort.ownerFactionId !== factionId) return; // Ownership flipped away.
    const cohortId = toCohortId(state.nextCohortId++);
    const fortGarrison: Cohort = {
      id: cohortId,
      factionId,
      armyId: null,
      settlementId: null,
      worldStructureId: fortTargetId,
      type: order.cohortType,
      aliveFigureCount: spec.figureCount,
      stamina: 100,
      morale: 100,
      xp: 0,
      armorBonus,
    };
    state.cohorts.set(cohortId, fortGarrison);
    if (!fort.garrisonCohortIds) fort.garrisonCohortIds = [];
    fort.garrisonCohortIds.push(cohortId);
    return;
  }

  // Branches 2 + 3 — army recruit. Decide target Army: attach or create.
  let armyId = order.armyId;
  if (armyId === null || !state.armies.has(armyId)) {
    const commanderId = toCommanderId(state.nextCommanderId++);
    state.commanders.set(commanderId, createCommander(commanderId));
    armyId = toArmyId(state.nextArmyId++);
    // v0M3.11: find a Settlement that lives in this province and spawn there.
    // Falls back to (0, 0) only if the province has no cities — a
    // scenario-fixture edge case, real play always has a capital.
    const spawnPos = findProvinceCityCell(state, province.id) ?? { x: 0, y: 0 };
    state.armies.set(armyId, createArmy(armyId, factionId, commanderId, spawnPos));
  }

  // Spawn the cohort.
  const cohortId = toCohortId(state.nextCohortId++);
  const cohort: Cohort = {
    id: cohortId,
    factionId,
    armyId,
    settlementId: null,
    type: order.cohortType,
    aliveFigureCount: spec.figureCount,
    stamina: 100,
    morale: 100,
    xp: 0,
    armorBonus,
  };
  state.cohorts.set(cohortId, cohort);

  // Attach to the Army's roster.
  const army = state.armies.get(armyId)!;
  army.cohortIds.push(cohortId);
}

/**
 * Find a cell to spawn a newly-recruited army on when the recruit
 * completes without a target Army id. Returns the first Settlement that lives
 * in the recruiting province (usually the capital); returns null if the
 * province has no cities (test-fixture edge case — real scenario boot
 * always assigns at least one city per faction capital).
 */
function findProvinceCityCell(
  state: GameState,
  provinceId: import('./state').ProvinceId,
): { x: number; y: number } | null {
  for (const city of state.settlements.values()) {
    if (city.provinceId === provinceId) {
      return { x: city.cell.x, y: city.cell.y };
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Step 5 — Cohort recovery (v0M3.11)
// ---------------------------------------------------------------------------

/**
 * Persistent stamina + morale recovery on the strategic map. Runs after
 * the province-scoped steps because it iterates cohorts, not provinces.
 *
 * Rules (Michael-locked 2026-07-14):
 *  - **Stamina** += `cohort_stamina_recovery_per_day / 5` per tick when the
 *    owning Army is NOT in a battle AND is sitting still (no plannedPath
 *    and no movementGoal). Capped at 100.
 *  - **Morale** += `cohort_morale_recovery_per_day / 5` per tick when the
 *    owning Army is NOT in a battle AND is inside a province owned by a
 *    same-team faction (friendly). Capped at 100.
 *  - Cohorts inside an active battle receive no map-side recovery — the
 *    battle-round loop owns their stamina/morale for the duration; the
 *    map-side clock is paused for them.
 *  - Recovery of stamina and morale is independent: an army moving through
 *    a friendly province still recovers morale but not stamina; an army
 *    idle in enemy territory recovers stamina but not morale.
 *
 * Wilderness (`provinceMap[cell] === 0`) is NEVER friendly — no morale
 * recovery for a cohort in the wild even if the owning faction owns
 * adjacent provinces.
 */
function stepCohortRecovery(state: GameState, context: SimContext): void {
  for (const cohort of state.cohorts.values()) {
    // v0M4.2.1: garrison cohorts (owner.kind === 'city') recover
    // trivially. They never move, they always sit in their home city
    // which is friendly by definition, and no field battle can touch
    // them. When a siege battle is layered on top (v0M4.2.5), the
    // battle-round loop owns their stamina/morale for the duration
    // just like it does for army cohorts (per §2.7.9), and this
    // block picks up recovery again once the siege concludes.
    if (cohort.settlementId !== null) {
      if (cohort.stamina < 100) {
        cohort.stamina = Math.min(100, cohort.stamina + STAMINA_RECOVERY_PER_TICK);
      }
      if (cohort.morale < 100) {
        cohort.morale = Math.min(100, cohort.morale + MORALE_RECOVERY_PER_TICK);
      }
      continue;
    }

    // Army cohort path — nullable armyId narrows here (all garrisons
    // handled above and continued out).
    if (cohort.armyId === null) continue; // Defensive: shouldn't happen given invariant.
    const army = state.armies.get(cohort.armyId);
    if (!army) continue;
    if (army.currentBattleId !== null) continue; // In battle — battle-round owns recovery.

    // MFix6.3 (2026-07-23): friendly-city ZoC recovery bonus. If the
    // army sits within `settlement_zoc_radius` of a city owned by the
    // cohort's OWN faction, multiply the per-tick recovery rate by
    // `friendly_settlement_zoc_recovery_multiplier`. Encourages resting
    // wounded armies near own cities.
    const nearFriendlyCity = isArmyInFriendlyCityZoc(state, army, cohort.factionId);
    const staminaRate = nearFriendlyCity
      ? STAMINA_RECOVERY_PER_TICK * FRIENDLY_CITY_RECOVERY_MULT
      : STAMINA_RECOVERY_PER_TICK;
    const moraleRate = nearFriendlyCity
      ? MORALE_RECOVERY_PER_TICK * FRIENDLY_CITY_RECOVERY_MULT
      : MORALE_RECOVERY_PER_TICK;

    // Stamina — recovers when sitting still.
    if (army.plannedPath === null && army.movementGoal === null) {
      if (cohort.stamina < 100) {
        cohort.stamina = Math.min(100, cohort.stamina + staminaRate);
      }
    }

    // Morale — recovers inside a friendly-team province.
    if (isArmyInFriendlyProvince(state, context, army, cohort.factionId)) {
      if (cohort.morale < 100) {
        cohort.morale = Math.min(100, cohort.morale + moraleRate);
      }
    }
  }
}

/**
 * MFix6.3 — true iff `army.position` sits within Chebyshev
 * `SETTLEMENT_ZOC_RADIUS` cells of any city owned by the cohort's OWN
 * faction. O(cities) per cohort; v1 has ~100 cities and a small number
 * of cohorts, so trivial.
 *
 * v0M7.4d (2026-07-25) — extended to OR-match friendly-owned Fort
 * WorldStructures per `MAP.md` §9.2 "provides Supplies to friendly
 * Armies inside its ZoC". Fort ZoC radius comes from the MapStructures
 * xlsx spec (`Fort.zocRadius`, default 4); falls back to
 * `SETTLEMENT_ZOC_RADIUS` if the sheet is absent. Same friendly-supply
 * semantics as cities — Fort acts as a mini-settlement for supply
 * purposes. `fort_supply_per_day` dial reserved for future
 * differentiation (currently unused; regen rate matches city ZoC).
 */
function isArmyInFriendlyCityZoc(
  state: GameState,
  army: { position: { x: number; y: number } },
  cohortFactionId: import('./state').FactionId,
): boolean {
  for (const city of state.settlements.values()) {
    const province = state.provinces.get(city.provinceId);
    if (!province) continue;
    if (province.ownerFactionId !== cohortFactionId) continue;
    const dx = Math.abs(army.position.x - city.cell.x);
    const dy = Math.abs(army.position.y - city.cell.y);
    if (Math.max(dx, dy) <= SETTLEMENT_ZOC_RADIUS) return true;
  }
  // v0M7.4d — also include friendly Forts (world buildables per §9).
  // v0M7.4d+siege2 (2026-07-26) — Forts under active siege do NOT
  // contribute supply/replenishment (matches settlement resource-
  // denial-while-besieged pattern per BATTLES.md §3.4). The
  // besieged Fort is materially cut off — friendlies inside its
  // ZoC lose the supply lifeline until the siege breaks or the Fort
  // is relieved by a friendly Army arriving on the Fort cell.
  if (state.worldStructures) {
    for (const ws of state.worldStructures.values()) {
      if (ws.kind !== 'fort') continue;
      if (ws.ownerFactionId !== cohortFactionId) continue;
      if (ws.siegeState != null) continue; // Besieged — no supply provision.
      const radius = FORT_ZOC_RADIUS;
      const dx = Math.abs(army.position.x - ws.cell.x);
      const dy = Math.abs(army.position.y - ws.cell.y);
      if (Math.max(dx, dy) <= radius) return true;
    }
  }
  return false;
}

/**
 * True iff `army.position` cell falls inside a Province owned by a
 * faction on the same `teamId` as `cohortFactionId`. False when:
 *  - `context.provinceMap` is absent (test fixtures — conservative default),
 *  - army is out of map bounds,
 *  - army is in wilderness (province mask = 0),
 *  - the province is unowned or owned by a different team.
 */
function isArmyInFriendlyProvince(
  state: GameState,
  context: SimContext,
  army: import('./state').Army,
  cohortFactionId: import('./state').FactionId,
): boolean {
  const pmap = context.provinceMap;
  if (!pmap) return false;
  const w = context.terrain.width;
  const h = context.terrain.height;
  const cx = Math.floor(army.position.x);
  const cy = Math.floor(army.position.y);
  if (cx < 0 || cx >= w || cy < 0 || cy >= h) return false;
  const mask = pmap[cy * w + cx];
  if (mask === undefined || mask === 0) return false; // Wilderness or OOB.
  const province = state.provinces.get(mask as unknown as import('./state').ProvinceId);
  if (!province || province.ownerFactionId === null) return false;
  const cohortFaction = state.factions.get(cohortFactionId);
  const provinceFaction = state.factions.get(province.ownerFactionId);
  if (!cohortFaction || !provinceFaction) return false;
  return cohortFaction.teamId === provinceFaction.teamId;
}

// ---------------------------------------------------------------------------
// Step 6 — Manpower generation + cap (MFix6.6.2, 2026-07-23)
// ---------------------------------------------------------------------------

/**
 * MFix6.6.2 (2026-07-23) — per-city manpower generation.
 *
 * For every Settlement, adds `population × generation_per_pop_per_day / ticks_per_day`
 * to `city.manpower`, capped at `population × cap_fraction_of_pop`.
 * Population is read from the city's containing Province (v1 rule:
 * every province has one major city, so population is a 1:1 proxy).
 *
 * Cities under active siege do NOT generate manpower — the resource-
 * denial rule (§3.4) already zeros production for the province; extend
 * the same denial to manpower for consistency.
 *
 * Neutral / unowned cities (wilderness) still generate manpower into
 * their pool — the pool is race-tagged, not faction-tagged, so a
 * player who captures the city inherits the accumulated pool.
 */
function stepManpowerGeneration(state: GameState): void {
  const perTickPerPop = MANPOWER_GEN_PER_POP_PER_DAY / TICKS_PER_GAME_DAY;
  for (const city of state.settlements.values()) {
    if (city.siegeState !== null) continue; // Under siege → no generation.
    const province = state.provinces.get(city.provinceId);
    if (!province) continue;
    const pop = province.population;
    const cap = Math.floor(pop * MANPOWER_CAP_FRACTION_OF_POP);
    const current = city.manpower ?? 0;
    if (current >= cap) continue;
    const next = Math.min(cap, current + pop * perTickPerPop);
    city.manpower = next;
  }
}

// ---------------------------------------------------------------------------
// Step 7 — Figure replenishment (MFix6.6.4, 2026-07-23)
// ---------------------------------------------------------------------------

/**
 * MFix6.6.4 (2026-07-23) — replace killed figures for army cohorts
 * sitting inside a friendly city's ZoC (Chebyshev radius `settlement_zoc_radius`).
 *
 * For each qualifying cohort:
 *   1. Compute how many figures it's down (spec.figureCount - aliveFigureCount).
 *   2. Find the CLOSEST friendly city with matching `manpowerRace` and
 *      at least 1 manpower.
 *   3. Restore up to REPLENISH_PER_TICK figures (rounded to whole
 *      figures via a per-cohort partial accumulator held on the cohort's
 *      xp field... no wait, that's fragile). Simpler v1: round the
 *      fractional per-tick to a whole-figure per-tick chance by
 *      probability. Actually simplest: allow fractional aliveFigureCount
 *      internally via a separate field. But we'd need schema change.
 *
 * v1 pragmatic choice: accumulate a per-city fractional counter across
 * ticks, restoring 1 figure to a specific cohort whenever the counter
 * exceeds 1. This is deterministic per tick (city-driven, not RNG).
 *
 * Actually simplest correct behaviour: floor the per-tick add to whole
 * figures + accumulate the fractional part on a hidden counter. But
 * that requires a schema field. For MVP: just apply the per-tick add
 * as a fractional accumulator using `stamina`'s modulo trick... no,
 * that's bad too.
 *
 * Cleanest MVP: use a module-scoped `Map<cohortId, fractionalAccumulator>`.
 * State survives across ticks in-process. Deterministic per session boot.
 * Post-v1 refactor: promote to Cohort schema field.
 */
const replenishAccumulator = new Map<number, number>();

function stepFigureReplenishment(state: GameState): void {
  if (REPLENISH_PER_TICK <= 0) return;
  const COHORTS = loadCohorts();

  for (const cohort of state.cohorts.values()) {
    // Skip garrisons (they replenish differently — the city IS their home).
    if (cohort.settlementId !== null) continue;
    if (cohort.armyId === null) continue;
    const army = state.armies.get(cohort.armyId);
    if (!army) continue;
    if (army.currentBattleId !== null) continue; // In battle — battle-round owns roster.

    const spec = COHORTS[cohort.type];
    if (!spec) continue;
    const maxFigures = spec.figureCount;
    if (cohort.aliveFigureCount >= maxFigures) {
      replenishAccumulator.delete(cohort.id as unknown as number);
      continue;
    }

    // Find closest friendly city with matching race + manpower.
    const cohortRace = cohort.cohortRace ?? 'Human';
    let bestCity: import('./state').Settlement | null = null;
    let bestDist = Infinity;
    for (const city of state.settlements.values()) {
      const province = state.provinces.get(city.provinceId);
      if (!province) continue;
      if (province.ownerFactionId !== cohort.factionId) continue;
      const race = city.manpowerRace ?? 'Human';
      if (race !== cohortRace) continue;
      const manpower = city.manpower ?? 0;
      if (manpower <= 0) continue;
      const dx = Math.abs(army.position.x - city.cell.x);
      const dy = Math.abs(army.position.y - city.cell.y);
      const cheb = Math.max(dx, dy);
      if (cheb > SETTLEMENT_ZOC_RADIUS) continue;
      if (cheb < bestDist) {
        bestDist = cheb;
        bestCity = city;
      }
    }
    if (!bestCity) {
      // Not in any friendly city ZoC — no replenishment this tick.
      continue;
    }

    // Accumulate fractional replenishment; when accumulator >= 1,
    // restore a whole figure and deduct 1 manpower.
    const cohortIdN = cohort.id as unknown as number;
    let acc = (replenishAccumulator.get(cohortIdN) ?? 0) + REPLENISH_PER_TICK;
    while (acc >= 1 && cohort.aliveFigureCount < maxFigures) {
      const currentManpower = bestCity.manpower ?? 0;
      if (currentManpower < 1) break;
      cohort.aliveFigureCount += 1;
      bestCity.manpower = currentManpower - 1;
      acc -= 1;
    }
    replenishAccumulator.set(cohortIdN, acc);
  }
}

// ---------------------------------------------------------------------------
// Public: income computation for the resource HUD (v0M3.9)
// ---------------------------------------------------------------------------
// Step 8 (v0M10.1 — 2026-07-24) — Army supply consumption / regeneration
// ---------------------------------------------------------------------------

/**
 * Advance each army's supply pool.
 *
 * Rules (v0M10 lock):
 *   - Army sits in a friendly settlement's ZoC (`isArmyInFriendlyCityZoc`)
 *     → regen at ARMY_SUPPLY_REGEN_PER_TICK per tick, capped at
 *     `army.supplyCap`. Draws from the owning faction's global
 *     `resources.supplies` ledger; if that ledger is empty this tick,
 *     regen skips (no partial fill).
 *   - Otherwise → consume ARMY_SUPPLY_CONSUMPTION_PER_FIGURE_PER_TICK ×
 *     total living figures. Clamps at 0.
 *
 * At v1 hitting 0 supplies is silent — no attrition / stamina / morale
 * consequence. A later MFix will layer a starvation effect on top of
 * this bookkeeping.
 *
 * Armies currently locked in battle skip the supply tick — battle
 * cohorts consume figure-count-based supplies elsewhere (BATTLES.md
 * missile_supply already models per-shot cost) so double-consumption
 * would double-charge.
 */
function stepArmySupply(state: GameState): void {
  for (const armyId of sortedIds(state.armies)) {
    const army = state.armies.get(armyId);
    if (!army) continue;
    // v0M10.4 (2026-07-24) — armies in battle still consume + regen
    // supplies. Per Michael's decision — combat is high-attrition and
    // the pool should reflect that.
    const cap = army.supplyCap ?? ARMY_SUPPLY_CAP;
    const current = army.supplies ?? cap;
    // In-ZoC check uses the shared helper (SETTLEMENT_ZOC_RADIUS dial).
    const inFriendlyZoC = isArmyInFriendlyCityZoc(state, army, army.ownerFactionId);
    if (inFriendlyZoC) {
      // Regen. Draw the smaller of (per-tick rate, remaining cap
      // headroom, faction ledger) from faction.resources.supplies.
      const faction = state.factions.get(army.ownerFactionId);
      if (faction && current < cap) {
        const factionSupplies = faction.resources.supplies;
        if (factionSupplies > 0) {
          const need = Math.min(ARMY_SUPPLY_REGEN_PER_TICK, cap - current, factionSupplies);
          army.supplies = current + need;
          faction.resources.supplies = factionSupplies - need;
        }
      }
    } else {
      // Consume. Total living figures across the army's cohorts.
      let livingFigures = 0;
      for (const cid of army.cohortIds) {
        const c = state.cohorts.get(cid);
        if (c) livingFigures += c.aliveFigureCount;
      }
      if (livingFigures > 0) {
        const drain = ARMY_SUPPLY_CONSUMPTION_PER_FIGURE_PER_TICK * livingFigures;
        army.supplies = Math.max(0, current - drain);
      }
    }
    // v0M10.4 (2026-07-24) — starvation drain. Runs regardless of
    // in/out-of-ZoC — if the army arrived in ZoC starving, it takes
    // this tick's damage before regen kicks it out of the 0-supply
    // state on subsequent ticks. Reads `army.supplies` after the
    // regen/consume step above.
    const suppliesAfter = army.supplies ?? cap;
    for (const cid of army.cohortIds) {
      const c = state.cohorts.get(cid);
      if (!c || c.aliveFigureCount === 0) continue;
      if (suppliesAfter <= 0) {
        // Phase 1: drain morale + stamina at their independent rates.
        // Once BOTH hit 0, phase 2 kicks in and starvationHp begins
        // draining. Drains apply in parallel — if morale hits 0 first
        // it just stops draining while stamina continues.
        if (c.morale > 0 || c.stamina > 0) {
          c.morale = Math.max(0, c.morale - STARVATION_MORALE_DRAIN_PER_TICK);
          c.stamina = Math.max(0, c.stamina - STARVATION_STAMINA_DRAIN_PER_TICK);
        } else {
          // Phase 2: shared HP pool per cohort. Initialise from
          // spec.maxHealthPerFigure on first entry. When it hits 0,
          // wipe the cohort's figures (they starved to death).
          const spec = COHORTS[c.type];
          const maxHp = spec?.maxHealthPerFigure ?? 60;
          const hp = c.starvationHp ?? maxHp;
          const newHp = hp - STARVATION_HP_DRAIN_PER_TICK;
          if (newHp <= 0) {
            c.aliveFigureCount = 0;
            c.starvationHp = 0;
          } else {
            c.starvationHp = newHp;
          }
        }
      } else {
        // Resupplied — reset starvationHp so subsequent starvation
        // events start from a full pool. Morale + stamina recover on
        // their own schedule via stepCohortRecovery.
        if (c.starvationHp !== undefined) c.starvationHp = undefined;
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Step 9 (v0M10.5 — 2026-07-24) — Officer Academy commander XP tick
// ---------------------------------------------------------------------------

/**
 * Per-tick commander XP grant from structures. Currently only Officer
 * Academy is a "Commander XP + N per day" source, but the helper is
 * generic — any structure whose effect string matches the pattern will
 * contribute.
 *
 * Rules:
 *   - Army must sit in a friendly settlement's ZoC (Chebyshev ≤
 *     SETTLEMENT_ZOC_RADIUS from the settlement cell).
 *   - Settlement's province must have the granting structure built.
 *   - Commander's current level must be BELOW OFFICER_ACADEMY_LEVEL_CAP
 *     (5). Above the cap, structure-XP is a no-op — battle-earned XP
 *     still applies via battle-conclusion.
 *
 * XP is fractional per tick (5/day / 25 ticks = 0.2 XP/tick). Stored
 * as-is on commander.xp (already a `number` field); battle-conclusion's
 * `Math.floor(xp / 1000) + 1` level formula handles fractional inputs
 * fine. Post-v1 refactor: reconcile with CommanderUnlocks xlsx thresholds.
 */
function stepCommanderStructureXp(state: GameState): void {
  for (const armyId of sortedIds(state.armies)) {
    const army = state.armies.get(armyId);
    if (!army) continue;
    const commander = state.commanders.get(army.commanderId);
    if (!commander) continue;
    // Level cap — early exit avoids the per-settlement scan when the
    // commander is already past the Officer Academy's benefit.
    if (commander.level >= OFFICER_ACADEMY_LEVEL_CAP) continue;

    // Find friendly settlements in ZoC and sum their commander-XP bonus.
    let bonusPerDay = 0;
    for (const settlement of state.settlements.values()) {
      const province = state.provinces.get(settlement.provinceId);
      if (!province) continue;
      if (province.ownerFactionId !== army.ownerFactionId) continue;
      const dx = Math.abs(army.position.x - settlement.cell.x);
      const dy = Math.abs(army.position.y - settlement.cell.y);
      if (Math.max(dx, dy) > SETTLEMENT_ZOC_RADIUS) continue;
      bonusPerDay += commanderXpPerDayBonus(province, STRUCTURES);
    }
    if (bonusPerDay > 0) {
      commander.xp += bonusPerDay / TICKS_PER_GAME_DAY;
      // v0M10.6 (2026-07-24) — xlsx-authoritative promotion via
      // CommanderUnlocks. Shared helper with battle-conclusion.ts so
      // both XP sources use one curve.
      const targetLevel = commanderLevelForXp(commander.xp, ECON_COMMANDER_LEVELS);
      if (targetLevel > commander.level) {
        commander.level = targetLevel;
      }
    }
  }
}

// ---------------------------------------------------------------------------

/**
 * Compute the per-game-time-second income rates for `factionId`. All
 * rates are gross (production only) except **Food**, which nets out
 * per-population consumption per ECONOMY.md §2.2. Client HUDs display
 * these directly as "+N.N/s" strings.
 *
 * Sources considered:
 *  - Gold: sum of (province.population × gold_per_citizen_per_sec) across
 *    all owned provinces.
 *  - Food: sum of (Farm production 1/s) − sum of (province.population ×
 *    supplies_per_citizen_per_sec). Can be negative if consumption exceeds
 *    production (population outstripping food supply).
 *  - Iron/Wood/Horses/Supplies/Learning: sum of (built structures with
 *    matching `produces` × ratePerSec). Only *completed* structures
 *    count — queued builds don't produce yet.
 *
 * v0M3.9 first pass: does NOT account for future losses (upcoming
 * recruit costs, structure gold costs). Player sees raw accrual rate.
 */
/**
 * v0M5.5 (2026-07-25) — per-province gold accrual over one game day.
 * Used by the espionage system's Steal drain (`ESPIONAGE.md §2.4`) —
 * the drain size is a percentage of this figure per active-Steal day.
 * Sums citizen contribution + completed-structure `Gold` production.
 * Returns 0 if the province is unowned.
 */
export function computeProvinceGoldPerDay(state: GameState, provinceId: import('./state').ProvinceId): number {
  const province = state.provinces.get(provinceId);
  if (!province || province.ownerFactionId === null) return 0;
  let goldPerSec = province.population * GOLD_PER_CITIZEN_PER_SEC;
  for (const built of province.structures) {
    const spec = STRUCTURES[built.name];
    if (!spec) continue;
    // v0M9 — pillaged-minor structures contribute nothing.
    if (isStructurePillaged(state, built)) continue;
    // Reuse the existing per-spec dispatcher with seconds=1 to get per-sec.
    const scratch: Resources = { gold: 0, iron: 0, wood: 0, horses: 0, supplies: 0, learning: 0 };
    addStructureProduction(scratch, spec, 1);
    goldPerSec += scratch.gold;
  }
  // Scale to per-game-day.
  return goldPerSec * (TICKS_PER_GAME_DAY * SECONDS_PER_TICK);
}

export function computeFactionIncome(state: GameState, factionId: import('./state').FactionId): Resources {
  const income: Resources = {
    gold: 0,
    iron: 0,
    wood: 0,
    horses: 0,
    supplies: 0,
    learning: 0,
  };

  for (const provinceId of sortedIds(state.provinces)) {
    const province = state.provinces.get(provinceId)!;
    if (province.ownerFactionId !== factionId) continue;

    // Gold from citizens.
    income.gold += province.population * GOLD_PER_CITIZEN_PER_SEC;
    // Food consumption from citizens (subtract; may go negative).
    income.supplies -= province.population * SUPPLIES_PER_CITIZEN_PER_SEC;

    // Structure production — reuse the existing per-spec dispatcher with
    // `seconds = 1` so the resource key routing lives in one place.
    // v0M9 — pillaged-minor structures contribute nothing to income.
    for (const built of province.structures) {
      const spec = STRUCTURES[built.name];
      if (!spec) continue;
      if (isStructurePillaged(state, built)) continue;
      addStructureProduction(income, spec, 1);
    }
  }
  return income;
}
